import { useCallback, useEffect, useRef, useState } from "react";
import { useHibobApi } from "../../../Integrations/HiBob/api";

export const POLL_INTERVAL_MS = 5000;
export const POLL_WINDOW_MS = 3 * 60 * 1000;

const toTime = (value) => {
	const time = value ? new Date(value).getTime() : NaN;
	return Number.isNaN(time) ? null : time;
};

// Loads GET /integrations/hibob/status and keeps polling it every 5 s while a
// sync is running, or for up to 3 minutes after Sync now until that sync is over.
export const useHibobSettings = () => {
	const { getStatus } = useHibobApi();

	// loading | ready | error
	const [state, setState] = useState("loading");
	const [data, setData] = useState(null);
	const [pollUntil, setPollUntil] = useState(null);
	const [requestedAt, setRequestedAt] = useState(null);
	// Bumped after every poll, so the next one is scheduled even when the answer is unchanged.
	const [polls, setPolls] = useState(0);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const load = useCallback(
		async ({ silent = false } = {}) => {
			if (!silent) {
				setState("loading");
			}
			try {
				const response = await getStatus();
				// No response: the session expired and the app is reloading to the login.
				if (!mounted.current || !response) {
					return;
				}
				if (response.status !== 200 || !response.data?.status) {
					if (!silent) {
						setState("error");
					}
					return;
				}
				setData(response.data);
				setState("ready");
			} catch (err) {
				if (mounted.current && !silent) {
					setState("error");
				}
			}
		},
		[getStatus],
	);

	useEffect(() => {
		load();
	}, [load]);

	const watchSync = useCallback(() => {
		const now = Date.now();
		setRequestedAt(now);
		setPollUntil(now + POLL_WINDOW_MS);
	}, []);

	const sync = data?.sync;
	const running = sync?.lastStatus === "running";
	// After Sync now, the run we wait for has not started (or not finished) until
	// lastStartedAt moves past the press.
	const startedAt = toTime(sync?.lastStartedAt);
	const waitingForRequested =
		requestedAt !== null && (startedAt === null || startedAt < requestedAt - 60 * 1000 || running);
	const shouldPoll =
		state === "ready" &&
		data?.status === "connected" &&
		(running || (pollUntil !== null && Date.now() < pollUntil && waitingForRequested));

	useEffect(() => {
		if (!shouldPoll) {
			return undefined;
		}
		const timer = setTimeout(async () => {
			await load({ silent: true });
			if (mounted.current) {
				setPolls((n) => n + 1);
			}
		}, POLL_INTERVAL_MS);
		return () => clearTimeout(timer);
	}, [shouldPoll, polls, load]);

	return { state, data, reload: load, watchSync, polling: shouldPoll };
};
